import React from "react";
import PropTypes from "prop-types";
import { useLocalStorageState, useRequest } from "ahooks";
import * as moment from "moment";
import { Spin } from "antd";

import * as AuthenticationConstants from "@/constants/authentication";
import * as service from "@/services/authentication";

export const ClientAuthContext = React.createContext();

const isCredentialExpired = (credential) => {
  if (credential == null || credential.expiresIn == null) {
    return true;
  }
  return moment(credential.expiresIn).isBefore(moment());
};

export const ClientAuthContextProvider = (props) => {
  const [clientCredential, setClientCredential] = useLocalStorageState(AuthenticationConstants.CLIENT_CREDENTIAL_TYPE);
  const { run } = useRequest(
    () => service.authenticateClientCredential(AuthenticationConstants.CLIENT_CREDENTIAL_VALUES),
    {
      manual: true,
      onSuccess: (accessToken) => {
        setClientCredential(accessToken);
      },
    }
  );

  React.useEffect(() => {
    if (clientCredential == null) {
      run();
      return;
    }
    if (isCredentialExpired(clientCredential)) {
      // Clear expired token and silently request a new one — no error UI
      setClientCredential();
      run();
    }
  }, []);

  const hasValidCredential = clientCredential != null && !isCredentialExpired(clientCredential);

  if (!hasValidCredential) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh" }}>
        <Spin size="large" tip="We're waking everyone up to start working" />
      </div>
    );
  }

  return (
    <ClientAuthContext.Provider value={{ clientCredential }}>
      {props.children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => React.useContext(ClientAuthContext);

ClientAuthContextProvider.propTypes = {
  children: PropTypes.any,
};
